import React, { createContext, useState, useEffect, useCallback, useRef, ReactNode, useContext } from 'react';
import i18n from 'i18next';
import { useAuth } from './AuthProvider';
import { getMyUserProfile } from '../services/UserProfileService';
import { IUserProfile } from '../interfaces/heavyDutyMachinery/IUserProfile';

interface UserProfileContextProps {
  userProfile: IUserProfile | undefined;
  hasUserProfile: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

interface UserProfileProviderProps {
  children: ReactNode;
}

const UserProfileContext = createContext<UserProfileContextProps | undefined>(undefined);

/**
 * Single source of truth for this app's own UserProfile (display name,
 * photo, phone, country, language) -- HeavyDutyMachinery's step 2 of registration,
 * deliberately separate from Authorization's User (see UserProfile's own
 * doc comment on the backend). Everywhere that used to read
 * displayName/photoUrl/preferredLanguage off useAuth() now reads from here
 * instead; hasUserProfile also gates the rest of the app until step 2 is
 * done, same pattern as AgeVerification's PENDING_AGE_VERIFICATION_KEY.
 */
export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState<IUserProfile | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  // ProtectedRoute gates the *entire* authenticated app behind `loading`
  // (full-page spinner in place of `children`, unmounting everything under
  // it) -- correct for the very first resolution of a session, wrong for
  // every later `refresh()` call. Settings' many "save one field"/toggle
  // handlers all call refresh() to re-sync userProfile after a successful
  // update, which used to flip this same `loading` flag and cause the whole
  // page to unmount/remount for what should be a quiet background sync --
  // visibly a full-page spinner flash, and (before Settings' own mobile
  // section state was made URL-derived) the reason mobile Settings used to
  // get kicked back to its section list. This ref tracks whether the first
  // resolution has already happened, so only *that* one sets `loading`.
  const hasLoadedOnceRef = useRef(false);

  // A fresh login flips isAuthenticated from false to true synchronously
  // (no authLoading transition involved, unlike the initial-mount check),
  // while the effect below that would re-fetch and reset `loading` only
  // runs after this render commits. Without this, ProtectedRoute could
  // render once in between with the stale loading=false/userProfile=undefined
  // from the signed-out state and redirect to /complete-profile before the
  // real fetch ever started. Adjusting state during render (React's
  // documented pattern for this) closes the gap by resetting `loading`
  // in the very same render isAuthenticated changes in. A fresh login is
  // also its own "first resolution" (a different account's profile) --
  // resetting hasLoadedOnceRef lets the effect's refresh() gate normally.
  const [syncedFor, setSyncedFor] = useState(isAuthenticated);
  if (isAuthenticated !== syncedFor) {
    setSyncedFor(isAuthenticated);
    setLoading(true);
    hasLoadedOnceRef.current = false;
  }

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setUserProfile(undefined);
      setLoading(false);
      hasLoadedOnceRef.current = true;
      return;
    }
    if (!hasLoadedOnceRef.current) setLoading(true);
    try {
      const profile = await getMyUserProfile();
      setUserProfile(profile);
      // Follows the account across devices instead of only living in the
      // browser's own i18next-detector cache (see PreferredLanguage on the
      // backend entity).
      if (profile?.preferredLanguage && profile.preferredLanguage !== i18n.language) {
        i18n.changeLanguage(profile.preferredLanguage);
      }
    } catch {
      setUserProfile(undefined);
    } finally {
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, [isAuthenticated]);

  // AuthProvider's own isAuthenticated starts false and only flips to true
  // (or stays false) once its token check resolves -- if this effect acted
  // on isAuthenticated=false before that check finishes, it would mark
  // loading=false and userProfile=undefined prematurely, and ProtectedRoute
  // would redirect to /complete-profile on every reload before the real
  // fetch below ever got a chance to run. Waiting for authLoading to clear
  // first closes that race.
  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  return (
    <UserProfileContext.Provider value={{ userProfile, hasUserProfile: !!userProfile, loading, refresh }}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = (): UserProfileContextProps => {
  const context = useContext(UserProfileContext);
  if (!context) throw new Error('useUserProfile must be used within a UserProfileProvider');
  return context;
};
